// src/app/modules/fournisseurs/FournisseurManagement.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
    Plus, Search, Edit2, Trash2, Building2, Users, Mail, X, Check, Filter,
    Briefcase, Save, Info, CheckCircle2, XCircle
} from "lucide-react";

import {
    fetchFournisseursRequest,
    createFournisseurRequest,
    updateFournisseurRequest,
    deleteFournisseurRequest,
} from "../../../../redux/actions/fournisseursActions";

import {
    fetchConsultantsRequest,
} from "../../../../redux/actions/consultantsActions";

import "../RessourcesStyles/FournisseurManagement.css";

const emptyForm = {
    nom: "",
    raisonSociale: "",
    nomContact: "",
    rc: "",
    reference: "",
    emailContact: "",
    emailCabinet: "",
    specialite: "",
};

const normalize = (v) => String(v || "").trim().toLowerCase();

const Modal = ({ open, title, onClose, footer, children, tabs }) => {
    if (!open) return null;
    return (
        <div className="for-modal-overlay" onMouseDown={onClose}>
            <div className="for-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="for-modal-header">
                    <h3>{title}</h3>
                    <button className="for-icon-btn" onClick={onClose}><X size={20} /></button>
                </div>
                {tabs && <div className="for-modal-tabs">{tabs}</div>}
                <div className="for-modal-body">{children}</div>
                {footer ? <div className="for-modal-footer">{footer}</div> : null}
            </div>
        </div>
    );
};

export default function FournisseurManagement() {
    const dispatch = useDispatch();

    const fournisseursState = useSelector((s) => s.fournisseurs || {});
    const consultantsState = useSelector((s) => s.consultants || {});

    const fournisseurs = fournisseursState.fournisseurs || [];
    const consultants = consultantsState.consultants || [];

    const loading = !!(fournisseursState.loading || consultantsState.loading);

    const [pageTab, setPageTab] = useState("ALL"); // ALL | WITH | WITHOUT
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const [open, setOpen] = useState(false);
    const [modalTab, setModalTab] = useState("INFOS"); // INFOS | CONSULTANTS
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    const [form, setForm] = useState(emptyForm);
    const [consultSearch, setConsultSearch] = useState("");
    const [selectedConsultantIds, setSelectedConsultantIds] = useState(new Set());

    useEffect(() => {
        dispatch(fetchFournisseursRequest());
        dispatch(fetchConsultantsRequest());
    }, [dispatch]);

    const filteredFournisseurs = useMemo(() => {
        const q = normalize(search);

        return (fournisseurs || []).filter((f) => {
            const list = f?.consultants || [];
            const has = Array.isArray(list) && list.length > 0;

            const okTab =
                pageTab === "ALL" ? true : pageTab === "WITH" ? has : !has;

            const nom = normalize(f?.nom);
            const rs = normalize(f?.raisonSociale);
            const ref = normalize(f?.reference);
            const sp = normalize(f?.specialite);

            const okSearch = !q || nom.includes(q) || rs.includes(q) || ref.includes(q) || sp.includes(q);
            return okTab && okSearch;
        });
    }, [fournisseurs, search, pageTab]);

    const totalPages = Math.ceil(filteredFournisseurs.length / itemsPerPage) || 1;

    const paginatedFournisseurs = useMemo(() => {
        return filteredFournisseurs.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [filteredFournisseurs, currentPage]);

    useEffect(() => {
        const total = Math.ceil(filteredFournisseurs.length / itemsPerPage) || 1;
        if (currentPage > total) {
            setCurrentPage(total);
        }
    }, [filteredFournisseurs.length, currentPage]);

    const filteredConsultantsForModal = useMemo(() => {
        const q = normalize(consultSearch);
        return (consultants || []).filter((c) => {
            const n = normalize(c?.nomContact);
            const e = normalize(c?.emailContact);
            const s = normalize(c?.specialite);
            return !q || n.includes(q) || e.includes(q) || s.includes(q);
        });
    }, [consultants, consultSearch]);

    const openAdd = () => {
        setIsEdit(false);
        setCurrentId(null);
        setForm(emptyForm);
        setModalTab("INFOS");
        setConsultSearch("");
        setSelectedConsultantIds(new Set());
        setOpen(true);
    };

    const openEdit = (f) => {
        setIsEdit(true);
        setCurrentId(f.id);

        setForm({
            nom: f.nom || "",
            raisonSociale: f.raisonSociale || "",
            nomContact: f.nomContact || "",
            rc: f.rc || "",
            reference: f.reference || "",
            emailContact: f.emailContact || "",
            emailCabinet: f.emailCabinet || "",
            specialite: f.specialite || "",
        });

        const ids = new Set((f?.consultants || []).map((c) => c.id));
        setSelectedConsultantIds(ids);

        setModalTab("INFOS");
        setConsultSearch("");
        setOpen(true);

        // au cas o? la liste consultants n'est pas charg?e
        if (!consultants?.length) dispatch(fetchConsultantsRequest());
    };

    const closeModal = () => {
        setOpen(false);
        setForm(emptyForm);
        setSelectedConsultantIds(new Set());
        setConsultSearch("");
    };

    const toggleConsultant = (id) => {
        setSelectedConsultantIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAllFiltered = () => {
        setSelectedConsultantIds((prev) => {
            const next = new Set(prev);
            filteredConsultantsForModal.forEach((c) => next.add(c.id));
            return next;
        });
    };

    const clearAll = () =>ToggleAll(false);

    const ToggleAll = (on) => {
        setSelectedConsultantIds((prev) => {
            if (!on) return new Set();
            const next = new Set(prev);
            (consultants || []).forEach((c) => next.add(c.id));
            return next;
        });
    };

    const validate = () => {
        if (!form.nom?.trim()) return "Nom obligatoire";
        return null;
    };

    const onSubmit = () => {
        const err = validate();
        if (err) {
            Swal.fire({ icon: "warning", text: err, timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            nom: form.nom.trim(),
            raisonSociale: form.raisonSociale?.trim() || null,
            nomContact: form.nomContact?.trim() || null,
            rc: form.rc?.trim() || null,
            reference: form.reference?.trim() || null,
            emailContact: form.emailContact?.trim() || null,
            emailCabinet: form.emailCabinet?.trim() || null,
            specialite: form.specialite?.trim() || null,
            consultantIds: Array.from(selectedConsultantIds),
        };

        if (isEdit && currentId) {
            dispatch(updateFournisseurRequest(currentId, payload)); // signature style "catalogues"
        } else {
            dispatch(createFournisseurRequest(payload));
        }
        closeModal();
    };

    const onDelete = (id) => {
        Swal.fire({
            title: "Supprimer ?",
            text: "Cette action est irr?versible",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#ee645f",
            cancelButtonColor: "#204385",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteFournisseurRequest(id));
        });
    };

    return (
        <div className="supplier-management-page for-page">
            <div className="supplier-management-header">
                <div className="supplier-management-header-info">
                    <h1>Gestion des fournisseurs</h1>
                </div>
                <div className="supplier-management-actions">
                    <button className="supplier-management-btn supplier-management-btn-primary" onClick={openAdd}>
                        <Plus size={16} /> Nouveau Fournisseur
                    </button>
                </div>
            </div>

            <div className="supplier-management-filter-card">
                <div className="supplier-management-tabs">
                    <button className={`supplier-management-tab ${pageTab === "ALL" ? "is-active" : ""}`} onClick={() => { setPageTab("ALL"); setCurrentPage(1); }}>
                        Tous
                    </button>
                    <button className={`supplier-management-tab ${pageTab === "WITH" ? "is-active" : ""}`} onClick={() => { setPageTab("WITH"); setCurrentPage(1); }}>
                        Avec consultants
                    </button>
                    <button className={`supplier-management-tab ${pageTab === "WITHOUT" ? "is-active" : ""}`} onClick={() => { setPageTab("WITHOUT"); setCurrentPage(1); }}>
                        Sans consultants
                    </button>
                </div>

                <div className="supplier-management-search-wrap">
                    <Search size={15} className="supplier-management-search-icon" />
                    <input
                        type="text"
                        className="supplier-management-search"
                        value={search}
                        onChange={e => {
                            setSearch(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Rechercher par nom, raison sociale..."
                    />
                </div>

                <div className="supplier-management-meta">
                    {loading ? "Chargement..." : `${filteredFournisseurs.length} fournisseur(s)`}
                </div>
            </div>

            <div className="supplier-management-table-card">
                <div className="supplier-management-table-wrap">
                    <table className="supplier-management-table">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Contact / Email</th>
                                <th>Type / Catégorie</th>
                                <th>Statut</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="supplier-management-empty-cell">Chargement en cours...</td>
                                </tr>
                            ) : filteredFournisseurs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="supplier-management-empty-cell">Aucun fournisseur trouvé</td>
                                </tr>
                            ) : (
                                paginatedFournisseurs.map((f) => (
                                    <tr key={f.id}>
                                        <td>
                                            <div className="supplier-management-title-cell">
                                                <Building2 size={16} className="supplier-management-title-icon" />
                                                <div>
                                                    <div className="supplier-management-title">{f.nom}</div>
                                                    <div className="supplier-management-subtitle">{f.raisonSociale || "—"}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="supplier-management-contact-cell">
                                                <div className="supplier-management-contact-name">{f.nomContact || "—"}</div>
                                                <div className="supplier-management-contact-mail">
                                                    <Mail size={12} />
                                                    <span>{f.emailCabinet || f.emailContact || "—"}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="supplier-management-badge supplier-management-badge--blue">{f.specialite || "—"}</span>
                                        </td>
                                        <td>
                                            <span className="supplier-management-badge">
                                                <Users size={12} />
                                                {(f?.consultants || []).length} consultant(s)
                                            </span>
                                        </td>
                                        <td>
                                            <div className="supplier-management-row-actions">
                                                <button className="supplier-management-icon-btn" title="Modifier" onClick={() => openEdit(f)}>
                                                    <Edit2 size={16} />
                                                </button>
                                                <button className="supplier-management-icon-btn supplier-management-icon-btn--danger" title="Supprimer" onClick={() => onDelete(f.id)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {filteredFournisseurs.length > 0 && (
                    <div className="cicada-pagination" style={{ margin: "12px 14px" }}>
                        <div className="cicada-pagination-info">
                            Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredFournisseurs.length)} sur {filteredFournisseurs.length} fournisseurs
                        </div>

                        <div className="cicada-pagination-actions">
                            <button
                                type="button"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            >
                                Précédent
                            </button>

                            <span>
                                Page {currentPage} / {totalPages}
                            </span>

                            <button
                                type="button"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            >
                                Suivant
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <Modal
                open={open}
                title={isEdit ? "Modification Fournisseur" : "Nouveau Fournisseur"}
                onClose={closeModal}
                tabs={
                    <>
                        <div className={`for-modal-tab ${modalTab === "INFOS" ? "active" : ""}`} onClick={() => setModalTab("INFOS")}>
                            Général
                        </div>
                        <div className={`for-modal-tab ${modalTab === "CONSULTANTS" ? "active" : ""}`} onClick={() => setModalTab("CONSULTANTS")}>
                            Consultants liés ({selectedConsultantIds.size})
                        </div>
                    </>
                }
                footer={
                    <>
                        <button className="for-btn for-btn-ghost" onClick={closeModal}>Annuler</button>
                        <button className="for-btn for-btn-primary" onClick={onSubmit}>
                            <Save size={16} /> {loading ? "Traitement..." : "Enregistrer"}
                        </button>
                    </>
                }
            >
                {modalTab === "INFOS" ? (
                    <div className="for-grid">
                        <div className="for-field">
                            <label>Nom du fournisseur *</label>
                            <input
                                value={form.nom}
                                onChange={(e) => setForm({ ...form, nom: e.target.value })}
                                placeholder="Intitulé public"
                            />
                        </div>

                        <div className="for-field">
                            <label>Référence interne</label>
                            <input
                                value={form.reference}
                                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                                placeholder="ex: FR-001"
                            />
                        </div>

                        <div className="for-field">
                            <label>Raison Sociale</label>
                            <input
                                value={form.raisonSociale}
                                onChange={(e) => setForm({ ...form, raisonSociale: e.target.value })}
                                placeholder="SARL, SA, SAS..."
                            />
                        </div>

                        <div className="for-field">
                            <label>RC (Registre Commerce)</label>
                            <input
                                value={form.rc}
                                onChange={(e) => setForm({ ...form, rc: e.target.value })}
                                placeholder="Numéro RC"
                            />
                        </div>

                        <div className="for-field">
                            <label>Domaine / Spécialité</label>
                            <input
                                value={form.specialite}
                                onChange={(e) => setForm({ ...form, specialite: e.target.value })}
                                placeholder="Management, IT, Soft Skills..."
                            />
                        </div>

                        <div className="for-field">
                            <label>Contact Principal</label>
                            <input
                                value={form.nomContact}
                                onChange={(e) => setForm({ ...form, nomContact: e.target.value })}
                                placeholder="Nom Complet"
                            />
                        </div>

                        <div className="for-field">
                            <label>Email Professionnel (Cabinet)</label>
                            <input
                                value={form.emailCabinet}
                                onChange={(e) => setForm({ ...form, emailCabinet: e.target.value })}
                                placeholder="contact@cabinet.com"
                            />
                        </div>

                        <div className="for-field">
                            <label>Email Direct (Contact)</label>
                            <input
                                value={form.emailContact}
                                onChange={(e) => setForm({ ...form, emailContact: e.target.value })}
                                placeholder="direct@cabinet.com"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="for-checklist-wrap">
                        <div className="for-checklist-header">
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <Search size={14} style={{ color: "#94a3b8" }} />
                                <input
                                    style={{ border: "none", background: "transparent", fontSize: 13, outline: "none", width: 220 }}
                                    value={consultSearch}
                                    onChange={(e) => setConsultSearch(e.target.value)}
                                    placeholder="Rechercher par nom ou email..."
                                />
                            </div>
                            <div style={{ display: "flex", gap: 10 }}>
                                <button className="for-btn for-btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }} onClick={() => ToggleAll(true)}>Tous</button>
                                <button className="for-btn for-btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }} onClick={() => ToggleAll(false)}>Aucun</button>
                            </div>
                        </div>

                        <div className="for-checklist-body">
                            <table className="for-checklist-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 40, textAlign: "center" }}>Status</th>
                                        <th>Consultant</th>
                                        <th>Expertise / Contact</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredConsultantsForModal.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>Aucun consultant disponible</td>
                                        </tr>
                                    ) : (
                                        filteredConsultantsForModal.map((c) => {
                                            const checked = selectedConsultantIds.has(c.id);
                                            return (
                                                <tr key={c.id} className={checked ? "selected" : ""} onClick={() => toggleConsultant(c.id)} style={{ cursor: "pointer" }}>
                                                    <td style={{ textAlign: "center" }}>
                                                        <div className={`for-checkbox ${checked ? "checked" : ""}`} style={{ margin: "auto" }}>
                                                            {checked && <Check size={12} />}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="for-strong">{c.nomContact}</div>
                                                        <div style={{ fontSize: 10, opacity: 0.6 }}>REF: {c.reference || "?"}</div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontWeight: 600, color: "#204385", fontSize: 12 }}>{c.specialite || "?"}</div>
                                                        <div style={{ fontSize: 11, color: "#64748b" }}>{c.emailContact || "?"}</div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
